import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../../../../axios-order';
import { Grid, CircularProgress } from "@material-ui/core";
import { Download as DownloadIcon, ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import Snackbar from "@material-ui/core/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';

// Import the same module components as Report9.js
import FourPointOne from '../ReportPageForGSTR9/FourPointOne';
import Five from '../ReportPageForGSTR9/Five';
import Six from '../ReportPageForGSTR9/Six';
import Seven from '../ReportPageForGSTR9/Seven';
import Eight from '../ReportPageForGSTR9/Eight';
import Nine from '../ReportPageForGSTR9/Nine';
import Ten from '../ReportPageForGSTR9/Ten';
import Fourteen from '../ReportPageForGSTR9/Fourteen';
import Fifteen from '../ReportPageForGSTR9/Fifteen';
import Sixteen from '../ReportPageForGSTR9/Sixteen';
import Seventeen from '../ReportPageForGSTR9/Seventeen';
import Eighteen from '../ReportPageForGSTR9/Eighteen';

const useStyles = makeStyles({
    swipeButton: {
        position: 'relative',
        width: '100%',
        height: '60px',
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
        borderRadius: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '4px',
        cursor: 'pointer',
        overflow: 'hidden',
        border: 'none',
        outline: 'none',
        boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
        transition: 'all 0.3s ease',
        '&:hover': {
            boxShadow: '0 6px 20px rgba(255, 107, 107, 0.6)',
            transform: 'translateY(-2px)',
        },
    },
    swipeButtonActive: {
        background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
    },
    swipeTrack: {
        position: 'absolute',
        top: '4px',
        left: '4px',
        right: '4px',
        bottom: '4px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '26px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
    },
    swipeHandle: {
        width: '52px',
        height: '52px',
        background: 'white',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        transition: 'all 0.3s ease',
        zIndex: 2,
        position: 'relative',
    },
    swipeText: {
        color: 'white',
        fontSize: '16px',
        fontWeight: '600',
        textAlign: 'center',
        flex: 1,
        userSelect: 'none',
        marginLeft: '20px',
    },
    swipeIcon: {
        color: '#ff6b6b',
        fontSize: '24px',
        transition: 'transform 0.3s ease',
    },
    swipeIconActive: {
        color: '#4CAF50',
        transform: 'rotate(360deg)',
    },
});

const SwipeButton = ({ onSwipe, isLoading, children }) => {
    const classes = useStyles();
    const [isDragging, setIsDragging] = useState(false);
    const [dragPosition, setDragPosition] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);

    const handleMouseDown = (e) => {
        setIsDragging(true);
        e.preventDefault();
    };

    const handleMouseMove = (e) => {
        if (!isDragging || isCompleted) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const maxWidth = rect.width - 60;
        const newPosition = Math.min(Math.max(0, e.clientX - rect.left - 30), maxWidth);
        setDragPosition(newPosition);

        if (newPosition >= maxWidth * 0.8) {
            setIsCompleted(true);
            setIsDragging(false);
            setTimeout(() => {
                onSwipe();
            }, 200);
        }
    };

    const handleMouseUp = () => {
        if (!isCompleted) {
            setDragPosition(0);
        }
        setIsDragging(false);
    };

    const handleTouchStart = (e) => {
        setIsDragging(true);
        e.preventDefault();
    };

    const handleTouchMove = (e) => {
        if (!isDragging || isCompleted) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const maxWidth = rect.width - 60;
        const touch = e.touches[0];
        const newPosition = Math.min(Math.max(0, touch.clientX - rect.left - 30), maxWidth);
        setDragPosition(newPosition);

        if (newPosition >= maxWidth * 0.8) {
            setIsCompleted(true);
            setIsDragging(false);
            setTimeout(() => {
                onSwipe();
            }, 200);
        }
    };

    const handleTouchEnd = () => {
        if (!isCompleted) {
            setDragPosition(0);
        }
        setIsDragging(false);
    };

    return (
        <div
            className={`${classes.swipeButton} ${isCompleted ? classes.swipeButtonActive : ''}`}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div className={classes.swipeTrack}>
                <div
                    className={classes.swipeHandle}
                    style={{
                        transform: `translateX(${dragPosition}px)`,
                        cursor: isDragging ? 'grabbing' : 'grab',
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                >
                    {isLoading ? (
                        <CircularProgress size={24} style={{ color: '#ff6b6b' }} />
                    ) : (
                        <ArrowForwardIcon
                            className={`${classes.swipeIcon} ${isCompleted ? classes.swipeIconActive : ''}`}
                        />
                    )}
                </div>
                <div className={classes.swipeText}>
                    {isCompleted ? 'Proceeded...' : children}
                </div>
            </div>
        </div>
    );
};

const InfographicCards = () => {
    const { reconId } = useParams();
    const [tableData, setTableData] = useState({});
    const [triggerLoader, setTriggerLoader] = useState(false);
    const [activeModule, setActiveModule] = useState(null);
    const [loading, setLoading] = useState(false);
    const [gstr9cLoading, setGstr9cLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState("warning");
    const [responseMessage, setResponseMessage] = useState("");
    const classes = useStyles();
    const history = useHistory();

    // Snackbar handlers
    const handleSnackbarOpen = (message, severity) => {
        setSnackbarOpen(true);
        setSnackbarSeverity(severity);
        setResponseMessage(message);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // Get card colors from first file
    const getCardColor = (index) => {
        const colors = [
            '#d7bde2', '#a9cce3', '#edbb99', '#aed6f1', '#f9e79f', '#a3e4d7',
            '#f5b7b1', '#d5a6bd', '#b7e4c7', '#ffd3a5', '#c8a2c8', '#b3d9ff',
            '#e6ccb3', '#d4b5d4', '#c7e9b4',
        ];
        return colors[index % colors.length];
    };

    // Get card icons from first file
    const getCardIcon = (index) => {
        const icons = [
            <svg key="financial-report" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                <path d="M8 8h8" />
                <path d="M8 12h8" />
                <path d="M8 16h4" />
                <path d="M16 16h2" />
            </svg>,
            <svg key="tax-calculation" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
                <path d="M9 9h6v6H9z" />
                <path d="M10 10v4" />
                <path d="M12 10v4" />
                <path d="M14 10v4" />
                <path d="M10 12h4" />
            </svg>,
            <svg key="tax-liability" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 2a10 10 0 0 0-10 10c0 5.5 4.5 10 10 10s10-4.5 10-10S17.5 2 12 2z" />
                <path d="M8 8c0 4 8 4 8 0" />
                <path d="M12 12v6" />
            </svg>,
            <svg key="itc-reconciliation" width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M4 4h16v16H4z" />
                <path d="M8 8h8v8H8z" />
                <path d="M10 10v4" />
                <path d="M14 10v4" />
            </svg>,
        ];
        return icons[index % icons.length];
    };

    const downloadBase64File = (contentType, base64Data, fileName) => {
        const linkSource = `data:${contentType};base64,${base64Data}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        downloadLink.click();
    };

    const genarateExcel = () => {
        setLoading(true);
        const contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const filename = `GSTR-9.xlsx`;

        const body = {
            'business_enity_id': localStorage.getItem("business_entity_id"),
            'financail_year': localStorage.getItem('gstr9_financail_year'),
            'recon_id': reconId
        };

        axios.post('/gstr9/recon_report', body)
            .then((res) => {
                downloadBase64File(contentType, res.data.file, filename);
                setLoading(false);
            }).catch((err) => {
                setLoading(false);
                console.error('Excel generation error:', err);
            });
    };

    const onSaveGstr9Summary = () => {
        const payLoadData = {
            'business_enity_id': localStorage.getItem("business_entity_id"),
            'gstr9_summary_data': tableData,
            'recon_id': reconId
        };

        axios.post('/save_gstr9_summary_data', payLoadData)
            .then((res) => {
                handleSnackbarOpen(res?.data?.message, 'success');
            })
            .catch((err) => {
                console.error('Save error:', err);
                handleSnackbarOpen('Failed to save data. Please try again.', 'error');
            });
    };

    const onPushGstr9c = () => {
        setGstr9cLoading(true);
        const payLoadData = {
            'business_entity_id': localStorage.getItem("business_entity_id"),
            'financial_year': localStorage.getItem('gstr9_financail_year'),
            'gstr9_recon_id': reconId
        };

        axios.post('/gstr9c_recon', payLoadData)
            .then((res) => {
                setGstr9cLoading(false);
                handleSnackbarOpen(res?.data?.message, 'success');
            })
            .catch((err) => {
                setGstr9cLoading(false);
                console.error('API Error:', err);
                handleSnackbarOpen('Failed to process request. Please try again.', 'error');
            });
    };

    const updateTableData = (name, data) => {
        console.log('updating table data.......... \n', name, data);
        const newTableData = { ...tableData, [name]: data };
        setTableData(newTableData);
        storeTableItems(newTableData);
    };

    const storeTableItems = (data) => {
        const four_n = data['4.Details of advances, inwards and outward supplies made during the financial year on which tax is payable']?.[14];
        const four_g1 = data['4.Details of advances, inwards and outward supplies made during the financial year on which tax is payable']?.[6];
        const four_g2 = data['4.Details of advances, inwards and outward supplies made during the financial year on which tax is payable']?.[7];
        const six_o = data['6.Details of ITC availed during the financial year']?.[23];
        const five_n = data['5.Details of outward supplies made during the financial year on which tax is not payable']?.[14];

        const tableItems = {
            four_n: four_n || {},
            four_g1: four_g1 || {},
            four_g2: four_g2 || {},
            six_o: six_o || {},
            five_n: five_n || {}
        };

        const tableItemsJSON = JSON.stringify(tableItems);
        localStorage.setItem('tableItems', tableItemsJSON);
    };

    const get_gstr9_report_data = () => {
        setTriggerLoader(true);
        axios.get(`gstr9_master_summary/${localStorage.getItem("business_entity_id")}/${reconId}`)
            .then((res) => {
                const records = res.data.records;
                setTableData(records);
                storeTableItems(records);
                setTriggerLoader(false);
            }).catch((err) => {
                setTriggerLoader(false);
                console.error('Error fetching data:', err);
            });
    };

    useEffect(() => {
        get_gstr9_report_data();
    }, []);

    const handleCardClick = (moduleNumber) => {
        setActiveModule(moduleNumber);
    };

    const handleCancelButton = () => {
        setActiveModule(null);
    };

    const renderModuleContent = () => {
        switch (activeModule) {
            case 4:
                return <FourPointOne handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['4.Details of advances, inwards and outward supplies made during the financial year on which tax is payable']} />;
            case 5:
                return <Five handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['5.Details of outward supplies made during the financial year on which tax is not payable']} />;
            case 6:
                return <Six handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['6.Details of ITC availed during the financial year']} />;
            case 7:
                return <Seven handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['7.Details of ITC Reversed and Ineligible ITC for the financial year']} />;
            case 8:
                return <Eight handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['8.Other ITC related information']} />;
            case 9:
                return <Nine handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['9.Details of tax paid as declared in returns filed during the financial year']} />;
            case 10:
                return <Ten handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['10, 11, 12 & 13. Particulars of the transactions for the financial year declared in returns of the next financial year till the specified period']} />;
            case 14:
                return <Fourteen handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['14.Differential tax paid on account of declaration in 10 & 11 above']} />;
            case 15:
                return <Fifteen handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['15.Particulars of Demands and Refunds']} />;
            case 16:
                return <Sixteen handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['16.Information on supplies received from composition taxpayers, deemed supply under section 143 and goods sent on approval basis']} />;
            case 17:
                return <Seventeen handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['17. HSN wise summary of Outward Supplies']} />;
            case 18:
                return <Eighteen handleBack={handleCancelButton} updateTableData={updateTableData} data={tableData['18. HSN wise summary of Inward Supplies']} />;
            default:
                return null;
        }
    };

    // Calculation functions
    const getValue = (key, field) => {
        if (tableData && tableData[key]) {
            const sum = tableData[key].reduce((acc, item) => {
                const accValue = parseFloat(acc) || 0;
                const itemValue = parseFloat(item[field]) || 0;
                return accValue + itemValue;
            }, 0);
            return sum;
        }
        return 0;
    };

    const getValue10 = (key, field) => {
        if (tableData && tableData[key]) {
            const sum = tableData[key].reduce((acc, item) => {
                if (item.name !== "Total turnover (5N +10-11)") {
                    const accValue = parseFloat(acc) || 0;
                    const itemValue = parseFloat(item[field]) || 0;
                    return accValue + itemValue;
                }
                return acc;
            }, 0);
            return sum;
        }
        return 0;
    };

    const getTableCustomeTotal = (key, index) => {
        if (!tableData || !tableData[key] || tableData[key].length === 0) {
            return { taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
        }

        const arrayLength = tableData[key].length;
        const targetIndex = arrayLength - index;

        if (targetIndex < 0 || targetIndex >= arrayLength) {
            return { taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
        }

        const lastItem = tableData[key][targetIndex];
        if (!lastItem) {
            return { taxableValue: 0, igst: 0, cgst: 0, sgst: 0, cess: 0 };
        }

        return {
            taxableValue: lastItem.taxable_value || 0,
            igst: lastItem.igst || 0,
            cgst: lastItem.cgst || 0,
            sgst: lastItem.sgst || 0,
            cess: lastItem.cess || 0,
        };
    };

    const getTable5Value = () => {
        const key = '5.Details of outward supplies made during the financial year on which tax is not payable';
        if (!tableData || !tableData[key] || tableData[key].length < 2) {
            return 0;
        }

        const arrayLength = tableData[key].length;
        const targetIndex = arrayLength - 2;

        if (targetIndex < 0) {
            return 0;
        }

        const lastItem = tableData[key][targetIndex];
        if (!lastItem) {
            return 0;
        }

        return lastItem.taxable_value || 0;
    };

    const getPaidthoroughCash = (key) => {
        if (tableData && tableData[key]) {
            const sum = tableData[key].reduce((acc, item) => {
                const accValue = parseFloat(acc) || 0;
                const itemValue = (parseFloat(item.igst) || 0) + (parseFloat(item.cgst) || 0) + (parseFloat(item.sgst) || 0) + (parseFloat(item.cess) || 0);
                return accValue + itemValue;
            }, 0);
            return sum;
        }
        return 0;
    };

    const getFormattedResults = (items) => {
        let categoryTotals = {
            "Refund claimed": 0,
            "Refund sanctioned": 0,
            "Refund pending": 0,
            "Demand of Taxes": 0,
            "Taxes paid": 0,
            "Demands pending": 0
        };

        if (items && items.length >= 1) {
            items.forEach(item => {
                const total = (parseFloat(item.taxable_value) || 0) + (parseFloat(item.igst) || 0) + (parseFloat(item.cgst) || 0) + (parseFloat(item.cess) || 0) + (parseFloat(item.sgst) || 0);
                switch (item.name) {
                    case "(A).Total Refund claimed":
                        categoryTotals["Refund claimed"] = total;
                        break;
                    case "(B).Total Refund sanctioned":
                        categoryTotals["Refund sanctioned"] = total;
                        break;
                    case "(D).Total Refund Pending":
                        categoryTotals["Refund pending"] = total;
                        break;
                    case "(E).Total demand of taxes":
                        categoryTotals["Demand of Taxes"] = total;
                        break;
                    case "(F).Total taxes paid in respect of E above":
                        categoryTotals["Taxes paid"] = total;
                        break;
                    case "(G).Total demands pending out of E above":
                        categoryTotals["Demands pending"] = total;
                        break;
                    default:
                        break;
                }
            });
        }
        return categoryTotals;
    };

    const getModuleNumber = (title) => {
        if (title.includes('4.Details of advances')) return 4;
        if (title.includes('5.Details of outward supplies')) return 5;
        if (title.includes('6.Details of ITC availed')) return 6;
        if (title.includes('7.Details of ITC Reversed')) return 7;
        if (title.includes('8.Other ITC related')) return 8;
        if (title.includes('9.Details of tax paid')) return 9;
        if (title.includes('10, 11, 12 & 13')) return 10;
        if (title.includes('14.Differential tax paid')) return 14;
        if (title.includes('15.Particulars of Demands')) return 15;
        if (title.includes('16.Information on supplies')) return 16;
        if (title.includes('17. HSN wise summary of Outward')) return 17;
        if (title.includes('18. HSN wise summary of Inward')) return 18;
        return null;
    };

    const getCalculatedValues = (key) => {
        const moduleNumber = getModuleNumber(key);

        const table4Data = getTableCustomeTotal('4.Details of advances, inwards and outward supplies made during the financial year on which tax is payable', 1);
        const table6Data = getTableCustomeTotal('6.Details of ITC availed during the financial year', 1);
        const table7Data = getTableCustomeTotal('7.Details of ITC Reversed and Ineligible ITC for the financial year', 2);
        const table8Data = getTableCustomeTotal('8.Other ITC related information', 2);
        const table5Value = getTable5Value();
        const table15Data = getFormattedResults(tableData["15.Particulars of Demands and Refunds"]);

        switch (moduleNumber) {
            case 4:
                return [
                    { label: 'Taxable value', value: table4Data['taxableValue'] },
                    { label: 'Integrated Tax', value: table4Data['igst'] },
                    { label: 'Central Tax', value: table4Data['cgst'] },
                    { label: 'State/UT Tax', value: table4Data['sgst'] },
                    { label: 'CESS', value: table4Data['cess'] }
                ];
            case 5:
                return [{ label: 'Value', value: table5Value }];
            case 6:
                return [
                    { label: 'Integrated Tax', value: table6Data['igst'] },
                    { label: 'Central Tax', value: table6Data['cgst'] },
                    { label: 'State/UT Tax', value: table6Data['sgst'] },
                    { label: 'CESS', value: table6Data['cess'] }
                ];
            case 7:
                return [
                    { label: 'Integrated Tax', value: table7Data['igst'] },
                    { label: 'Central Tax', value: table7Data['cgst'] },
                    { label: 'State/UT Tax', value: table7Data['sgst'] },
                    { label: 'CESS', value: table7Data['cess'] }
                ];
            case 8:
                return [
                    { label: 'Integrated Tax', value: table8Data['igst'] },
                    { label: 'Central Tax', value: table8Data['cgst'] },
                    { label: 'State/UT Tax', value: table8Data['sgst'] },
                    { label: 'CESS', value: table8Data['cess'] }
                ];
            case 9:
                return [
                    { label: 'Tax payable', value: getValue('9.Details of tax paid as declared in returns filed during the financial year', 'tax_payble') },
                    { label: 'Paid through Cash', value: getValue('9.Details of tax paid as declared in returns filed during the financial year', 'paid_through_itc') },
                    { label: 'Paid through ITC', value: getPaidthoroughCash('9.Details of tax paid as declared in returns filed during the financial year') }
                ];
            case 10:
                return [
                    { label: 'Taxable value', value: getValue10('10, 11, 12 & 13. Particulars of the transactions for the financial year declared in returns of the next financial year till the specified period', 'taxable_value') },
                    { label: 'Integrated Tax', value: getValue10('10, 11, 12 & 13. Particulars of the transactions for the financial year declared in returns of the next financial year till the specified period', 'igst') },
                ];
            case 14:
                return [
                    { label: 'Taxable value', value: getValue('14.Differential tax paid on account of declaration in 10 & 11 above', 'tax_payble') },
                    { label: 'Tax Paid', value: getValue('14.Differential tax paid on account of declaration in 10 & 11 above', 'paid_through_itc') }
                ];
            case 15:
                return [
                    { label: 'Refund claimed', value: table15Data['Refund claimed'] },
                    { label: 'Refund sanctioned', value: table15Data['Refund sanctioned'] },
                    { label: 'Refund pending', value: table15Data['Refund pending'] },
                    { label: 'Demand of Taxes', value: table15Data['Demand of Taxes'] },
                    { label: 'Taxes paid', value: table15Data['Taxes paid'] },
                    { label: 'Demands pending', value: table15Data['Demands pending'] }
                ];
            case 16:
                return [
                    { label: 'Taxable value', value: getValue('16.Information on supplies received from composition taxpayers, deemed supply under section 143 and goods sent on approval basis', 'taxable_value') },
                    { label: 'Integrated Tax', value: getValue('16.Information on supplies received from composition taxpayers, deemed supply under section 143 and goods sent on approval basis', 'igst') },
                ];
            case 17:
                return [
                    { label: 'Taxable value', value: getValue('17. HSN wise summary of Outward Supplies', 'taxable_value') },
                    { label: 'Integrated Tax', value: getValue('17. HSN wise summary of Outward Supplies', 'igst') },
                ];
            case 18:
                return [
                    { label: 'Taxable value', value: getValue('18. HSN wise summary of Inward Supplies', 'taxable_value') },
                    { label: 'Integrated Tax', value: getValue('18. HSN wise summary of Inward Supplies', 'igst') },
                ];
            default:
                return [{ label: 'No data', value: 0 }];
        }
    };

    const handleBack = () => {
        history.goBack();
    };

    if (triggerLoader) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </div>
        );
    }

    if (activeModule) {
        return renderModuleContent();
    }

    return (
        <>
            <div style={{ padding: '2px', backgroundColor: '#f8fafc' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button className="button-outlined-primary" style={{ marginLeft: "0px", width: "10%" }} onClick={handleBack}>
                        BACK
                    </button>
                </div>
            </div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '15px' }}>
                {Object.keys(tableData).map((sectionTitle, index) => {
                    const moduleNumber = getModuleNumber(sectionTitle);
                    const calculatedValues = getCalculatedValues(sectionTitle);

                    return (
                        <div
                            key={index}
                            className={`card-${index + 5}`}
                            style={{
                                background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                                borderRadius: '20px',
                                padding: '0',
                                minWidth: '280px',
                                flex: '1 1 calc(33.33% - 10px)',
                                maxWidth: 'calc(33.33% - 10px)',
                                height: '320px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                color: '#333',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            }}
                            onClick={() => handleCardClick(moduleNumber)}
                            tabIndex={0}
                            role="button"
                        >
                            <style>
                                {`
                                    .card-${index + 5}:hover, .card-${index + 5}:focus {
                                        transform: scale(1.02);
                                        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
                                    }
                                    .card-${index + 5}:hover .card-background-${index + 5}, 
                                    .card-${index + 5}:focus .card-background-${index + 5} {
                                        background: linear-gradient(135deg, ${getCardColor(index)} 0%, ${getCardColor(index)}FF 100%);
                                    }
                                `}
                            </style>
                            <div
                                className={`card-background-${index + 5}`}
                                style={{
                                    position: 'absolute',
                                    top: '0',
                                    right: '0',
                                    width: '60%',
                                    height: '100%',
                                    background: `linear-gradient(135deg, ${getCardColor(index)} 0%, ${getCardColor(index)}DD 100%)`,
                                    borderRadius: '0 20px 20px 0',
                                    clipPath: 'ellipse(70% 100% at 100% 0%)',
                                    transition: 'background 0.2s ease',
                                }}
                            />
                            <div style={{ position: 'absolute', top: '15px', right: '15px', color: 'white', fontSize: '14px', fontWeight: 'bold', zIndex: 2 }}>
                                {index + 1}
                            </div>
                            <div style={{ padding: '15px', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                                <div style={{ marginTop: '40px', marginBottom: '15px', color: '#666' }}>
                                    {getCardIcon(index)}
                                </div>
                                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#333' }}>
                                    {sectionTitle}
                                </h3>
                                <div style={{ fontSize: '12px', color: '#333', marginBottom: '10px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginBottom: '5px' }}>
                                        <span>Description</span>
                                        <span>Value</span>
                                    </div>
                                    {calculatedValues.slice(0, 2).map((item, itemIndex) => (
                                        <div key={itemIndex} style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px', fontSize: '11px' }}>
                                            <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {item.label}
                                            </span>
                                            <span>₹{(item.value || 0).toLocaleString()}</span>
                                        </div>
                                    ))}
                                    {calculatedValues.length > 2 && (
                                        <p style={{ marginTop: '3px', fontSize: '10px', color: '#666' }}>
                                            +{calculatedValues.length - 2} more items
                                        </p>
                                    )}
                                </div>
                                <button
                                    style={{
                                        position: 'absolute',
                                        bottom: '15px',
                                        left: '15px',
                                        background: `linear-gradient(135deg, ${getCardColor(index)} 0%, ${getCardColor(index)}AA 100%)`,
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '20px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        zIndex: 2,
                                        transition: 'background 0.2s ease',
                                    }}
                                >
                                    Read More
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Action Buttons Section */}
            <div style={{ padding: '20px', backgroundColor: '#f8fafc' }}>
                <Grid container spacing={5} style={{ textAlign: "center", marginBottom: "20px" }}>
                    <Grid item xs={4}>
                        <div onClick={onSaveGstr9Summary} style={{ width: "100%", height: "32px", background: '#191970', color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "500", transition: "background-color 0.3s ease" }}>
                            Save
                        </div>
                    </Grid>
                    <Grid item xs={4}>
                        <LoadingButton loading={loading} variant="contained" style={{ width: "100%", height: "32px", background: loading ? "#355E3B" : '#355E3B' }} onClick={genarateExcel}>
                            <span><DownloadIcon />Generate Excel</span>
                        </LoadingButton>
                    </Grid>
                    <Grid item xs={4}>
                        <div style={{ width: "100%", height: "32px", background: '#51414F', color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "500", transition: "background-color 0.3s ease" }}>
                            Compute Liabilities
                        </div>
                    </Grid>
                </Grid>

                <Grid container spacing={5} justifyContent="center">
                    <Grid item xs={3}>
                        <div style={{ borderRadius: "12px", padding: "4px" }}>
                            <SwipeButton onSwipe={onPushGstr9c} isLoading={gstr9cLoading}>
                                Swipe to Proceed to 9c
                            </SwipeButton>
                        </div>
                    </Grid>
                </Grid>
            </div>

            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                open={snackbarOpen}
                autoHideDuration={5000}
                onClose={handleSnackbarClose}
                style={{ maxWidth: '25%' }}
            >
                <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbarSeverity}>
                    {responseMessage}
                </MuiAlert>
            </Snackbar>
        </>
    );
};

export default InfographicCards;